import { useId, useMemo, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { without } from '@/components/catalog/form-utils';
import { ApiError, api } from '@/lib/api';
import type {
  ApiKey,
  ApiKeyCreated,
  ApiKeyCreateInput,
  ApiKeyScope,
  ApiKeyUpdateInput,
} from '@/lib/types';
import { useUserOptions } from '@/lib/use-user-options';
import {
  Button,
  Combobox,
  type ComboboxOption,
  Field,
  Input,
  Modal,
  MultiCombobox,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import {
  MAX_LENGTHS,
  RATE_LIMIT_BLANK_MEANS_GLOBAL,
  asScopes,
  rateLimitError,
  rateLimitPayload,
  scopeOptions,
  scopesChanged,
} from './api-key-rules';

export interface ApiKeyFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Omitted = create. Provided = edit. */
  apiKey?: ApiKey;
  /**
   * A key was CREATED, and the argument carries the plaintext token.
   *
   * 🔴 Separate from {@link onSaved} deliberately: the caller must open the
   * token dialog on this path and must not on the other, and a single callback
   * carrying an optional token is one `if` away from a create whose token is
   * never shown — which costs a key nobody can use.
   */
  onCreated: (created: ApiKeyCreated) => void;
  /** An existing key was updated. No token is involved. */
  onSaved: () => void;
}

/**
 * Create or edit an API key.
 *
 * ── What a PATCH may carry ────────────────────────────────────────────────
 * `PATCH /api-keys/:id` takes the NAME, the RATE LIMIT and the SCOPES, and
 * nothing else. So on edit this form renders `purpose` and the acting user as
 * plain text rather than as disabled controls: a disabled box reads as "you may
 * not change this", where these are not changeable by anybody through this
 * endpoint. **A key acts as a user and that identity is fixed at creation** —
 * moving it would silently re-point every permission the credential resolves.
 *
 * ── Why the scopes control can send nothing ───────────────────────────────
 * A scopes write REPLACES the whole set, so `[]` CLEARS and an absent key
 * leaves them alone. The payload is built from `changedKeys`, and `scopes` only
 * reaches that list when {@link scopesChanged} says the SET moved — compared by
 * content, order-independently. An untouched control therefore produces no
 * `scopes` key at all, and the only route to `[]` is somebody emptying a set
 * that had something in it, which is a real instruction.
 */
interface FormValues {
  name: string;
  purpose: string;
  /** The picker's value; the id is a number on the wire. */
  userId: string;
  rateLimitPerMinute: string;
  /**
   * ⚠️ **Typed as the wire's closed union, not `string[]`** — so a payload can
   * be assembled without a cast at the point of sending, where a cast is
   * easiest to forget. The ONE narrowing happens at the control's `onChange`
   * through {@link asScopes}, which carries the argument for why it is sound.
   */
  scopes: ApiKeyScope[];
}

function toFormValues(apiKey?: ApiKey): FormValues {
  return {
    name: apiKey?.name ?? '',
    purpose: apiKey?.purpose ?? '',
    userId: '',
    /*
     * `null` is the global budget and reads as an EMPTY box, never as `0`. A
     * `?? 0` here would open every unlimited key's form claiming a limit of
     * zero, and saving anything else on that form would then store it.
     */
    rateLimitPerMinute:
      apiKey?.rateLimitPerMinute === null || apiKey?.rateLimitPerMinute === undefined
        ? ''
        : String(apiKey.rateLimitPerMinute),
    scopes: apiKey?.scopes ? [...apiKey.scopes] : [],
  };
}

/**
 * The `path` values a 422 may name that this form can actually MARK, per mode.
 *
 * ⚠️ **Derived from the form's own value object**, so the list cannot drift
 * away from the controls — `toFormValues()` is keyed on exactly what this form
 * holds.
 *
 * ⚠️ **It NARROWS on edit, because the rendered set is narrower.** `purpose`
 * and `userId` are create-only; a 422 naming either on an edit binds to no
 * control on screen, so counting it would produce "1 field needs attention
 * below" with nothing highlighted. It belongs in the form-level banner instead.
 * Same shape as `QuoteFormModal`'s `knownWriteKeys(isEdit)`.
 */
const CREATE_ONLY_KEYS: readonly string[] = ['purpose', 'userId'];

function knownWriteKeys(isEdit: boolean): readonly string[] {
  const all = Object.keys(toFormValues());
  return isEdit ? all.filter((key) => !CREATE_ONLY_KEYS.includes(key)) : all;
}

export function ApiKeyFormModal({
  open,
  onClose,
  apiKey,
  onCreated,
  onSaved,
}: ApiKeyFormModalProps): ReactElement | null {
  if (!open) return null;
  return (
    <ApiKeyForm
      key={apiKey?.id ?? 'new'}
      onClose={onClose}
      apiKey={apiKey}
      onCreated={onCreated}
      onSaved={onSaved}
    />
  );
}

function ApiKeyForm({
  onClose,
  apiKey,
  onCreated,
  onSaved,
}: {
  onClose: () => void;
  apiKey?: ApiKey;
  onCreated: (created: ApiKeyCreated) => void;
  onSaved: () => void;
}) {
  const formId = useId();
  const firstFieldRef = useRef<HTMLElement | null>(null);
  const isEdit = apiKey !== undefined;

  const baseline = useMemo(() => toFormValues(apiKey), [apiKey]);
  const [values, setValues] = useState<FormValues>(baseline);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const errors = useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);

  // Only fetched on create — the acting user is fixed after that, so an edit
  // must not spend a request against the office's shared budget reading a
  // directory it cannot offer.
  const staff = useUserOptions(!isEdit);

  /**
   * The keys whose VALUE has moved, which is the only list `buildPayload`
   * walks.
   *
   * ⚠️ **`scopes` is compared as a SET and every other key as a trimmed
   * string.** A sequence comparison on scopes would report a key as edited
   * whenever the server's order and the picker's order disagreed, and the save
   * would then send a whole-set replace on a visit that changed nothing.
   */
  const changedKeys = useMemo(() => {
    const keys = Object.keys(baseline) as (keyof FormValues)[];
    return keys.filter((key) => {
      if (key === 'scopes') return scopesChanged(baseline.scopes, values.scopes);
      const next = values[key];
      const previous = baseline[key];
      return typeof next === 'string' && typeof previous === 'string'
        ? next.trim() !== previous.trim()
        : next !== previous;
    });
  }, [values, baseline]);
  const isDirty = changedKeys.length > 0;

  function setField<K extends keyof FormValues>(key: K, next: FormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: next }));
    setClientErrors((prev) => (key in prev ? without(prev, key) : prev));
    setServerErrors((prev) => (key in prev ? without(prev, key) : prev));
  }

  /*
   * ⛔ **THE SCOPES KEY IS EMITTED ONLY FROM `changedKeys`, AND THAT IS THE
   * GUARD RATHER THAN A STYLE.** `[]` clears every scope; an absent key leaves
   * them alone. Assigning `payload.scopes = values.scopes` unconditionally
   * would re-assert the whole set on every save — harmless-looking, and it is
   * how a scope this bundle does not know about gets destroyed by somebody
   * editing a name (see `scopeOptions`).
   *
   * ⚠️ **Nothing here sends an actor id.** The server takes the acting user
   * from the bearer token; `created_by_id` is its business.
   */
  function buildPayload(): ApiKeyCreateInput | ApiKeyUpdateInput {
    if (isEdit) {
      const payload: ApiKeyUpdateInput = {};
      for (const key of changedKeys) {
        if (key === 'name') {
          payload.name = values.name.trim();
        } else if (key === 'rateLimitPerMinute') {
          // Blank resolves to `null` — the global budget — and never to `0`.
          payload.rateLimitPerMinute = rateLimitPayload(values.rateLimitPerMinute);
        } else if (key === 'scopes') {
          payload.scopes = values.scopes;
        }
        // `purpose` and `userId` are unreachable here: neither control renders
        // on edit, so neither can appear in `changedKeys`. Falling through
        // silently rather than throwing keeps a future create-only key from
        // breaking an unrelated save.
      }
      return payload;
    }

    const payload: ApiKeyCreateInput = {
      name: values.name.trim(),
      purpose: values.purpose.trim(),
      userId: Number(values.userId),
    };
    /*
     * Both optional keys are OMITTED when they carry nothing, rather than sent
     * empty. On a create there is no stored set for `[]` to destroy, so this is
     * not the replace-semantics rule — it is the narrower one: do not send what
     * the server has a default for, because "harmless" is a property of today's
     * schema.
     */
    if (values.scopes.length > 0) payload.scopes = values.scopes;
    const limit = rateLimitPayload(values.rateLimitPerMinute);
    if (limit !== null) payload.rateLimitPerMinute = limit;
    return payload;
  }

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};

    const name = values.name.trim();
    if (name === '') found.name = 'A name is required.';
    else if (name.length > MAX_LENGTHS.name) found.name = 'Must be 100 characters or fewer.';

    if (!isEdit) {
      const purpose = values.purpose.trim();
      if (purpose === '') found.purpose = 'A purpose is required.';
      else if (purpose.length > MAX_LENGTHS.purpose) {
        found.purpose = 'Must be 64 characters or fewer.';
      }
      /*
       * Required because the column is, and the reason is worth knowing: a key
       * authenticates AS a user, so every permission it resolves and every
       * audit row it writes hangs off this choice. There is no "no user"
       * option to offer.
       */
      if (values.userId.trim() === '') found.userId = 'Choose the account this key acts as.';
    }

    const limitError = rateLimitError(values.rateLimitPerMinute);
    if (limitError) found.rateLimitPerMinute = limitError;

    return found;
  }

  async function handleSubmit(event: FormEvent) {
    /*
     * Both, and in this order. A portalled modal still propagates through the
     * REACT tree, so a form opened from inside another modal's form submits
     * that one too — which created a half-filled record in this repo once.
     */
    event.preventDefault();
    event.stopPropagation();

    const found = validate();
    setClientErrors(found);
    setServerErrors({});
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    const payload = buildPayload();
    // A PATCH with no keys is a 422. Save is already disabled when nothing has
    // changed; this is the belt to that pair of braces.
    if (Object.keys(payload).length === 0) {
      setFormError('Nothing has changed yet.');
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        await api.updateApiKey(apiKey.id, payload as ApiKeyUpdateInput);
        onSaved();
      } else {
        const created = await api.createApiKey(payload as ApiKeyCreateInput);
        /*
         * ⛔ **THE RESPONSE IS HANDED STRAIGHT UP AND IS NEVER LOGGED, STORED
         * OR STRINGIFIED HERE.** It carries the only copy of the plaintext
         * token that will ever exist outside the caller that uses it — so this
         * line is also the reason there is no `catch` that reports the body,
         * and no retry that holds it.
         */
        onCreated(created.data);
      }
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * Split against this form's OWN rendered field list, never against the
         * shape of the path. `unroutable` is the empty-path `.strict()` and
         * "at least one field" refusals, plus anything naming a key this mode
         * does not render — a 422 about `purpose` on an EDIT binds to no
         * control, and rendering it at form level is the only way it is seen.
         */
        const { fields, unroutable } = caught.routeFieldErrors(knownWriteKeys(isEdit));
        setServerErrors(fields);
        if (unroutable.length > 0) setFormError(unroutable.join(' '));
        else if (Object.keys(fields).length === 0) setFormError(caught.message);
      } else {
        setFormError('Could not reach the server. Check your connection and try again.');
      }
      setSubmitting(false);
    }
  }

  const fieldErrorCount = Object.keys(errors).length;
  const saveDisabled = isEdit
    ? !isDirty
    : values.name.trim() === '' || values.purpose.trim() === '' || values.userId.trim() === '';

  const userOptions: ComboboxOption[] = useMemo(
    () =>
      staff.users.map((user) => ({
        value: String(user.id),
        // A `name` can be an empty string on some staff records, and an option
        // with no label is unpickable — the email is always there.
        label: user.name.trim() || user.email,
        hint: user.email,
      })),
    [staff.users],
  );

  /*
   * ⛔ **"WE DO NOT KNOW WHO IS ON STAFF" IS NOT "THERE IS NOBODY".** A failed
   * directory read must never arrive at this picker as an innocent empty list:
   * the reader would conclude no account can hold a key and go looking for a
   * permissions problem that does not exist.
   */
  const userFieldError = staff.failed
    ? 'The staff list could not be read, so there is nobody to choose from. Close this and try again.'
    : errors.userId;

  const scopeChoices = useMemo(() => scopeOptions(values.scopes), [values.scopes]);

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      dismissOnBackdrop={false}
      initialFocusRef={firstFieldRef}
      title={isEdit ? `Edit ${apiKey.name}` : 'New API key'}
      footer={
        <>
          {isEdit && !isDirty && (
            <p className="self-center text-xs text-fg-muted sm:mr-auto">No changes yet.</p>
          )}
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={saveDisabled}
          >
            {isEdit ? 'Save changes' : 'Create key'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-5">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        {/*
          * ⛔ **PROTECTED — this is not helper text.** It states the one
          * consequence of pressing Create that cannot be undone, at the moment
          * before it is pressed, and nothing else on this form can say it.
          */}
        {!isEdit && (
          <Notice tone="warning">
            The token is shown once, when the key is created, and cannot be recovered afterwards.
          </Notice>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div
            className="sm:col-span-2"
            ref={(node) => {
              firstFieldRef.current = node?.querySelector('input[name="name"]') ?? null;
            }}
          >
            <Field label="Name" required error={errors.name}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  name="name"
                  value={values.name}
                  maxLength={MAX_LENGTHS.name}
                  invalid={Boolean(errors.name)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('name', event.target.value)}
                />
              )}
            </Field>
          </div>

          {isEdit ? (
            /*
             * Plain text, not a disabled input. `PATCH /api-keys/:id` takes
             * name, rate limit and scopes; purpose is not among them, so a
             * greyed box would say "you may not change this" about something
             * nobody can change through this endpoint at all.
             */
            <div className="grid gap-3 sm:col-span-2 sm:grid-cols-2">
              <div className="min-w-0">
                <p className="m-0 text-xs font-medium text-fg-secondary">Purpose</p>
                <p className="mt-1 mb-0 text-sm text-fg [overflow-wrap:anywhere]">
                  {apiKey.purpose}
                </p>
              </div>
              {/*
                * ⚠️ **Shown, and not editable, because re-pointing it is not an
                * edit at all.** Every request this key makes is attributed to
                * this person in the audit trail; moving it would re-attribute
                * everything from that moment while the trail keeps every past
                * row under the old name. A key that should act as somebody else
                * is a new key and an old one revoked. It is absent from
                * `ApiKeyUpdateInput` structurally, so this form could not send
                * it even by accident.
                */}
              <div className="min-w-0">
                <p className="m-0 text-xs font-medium text-fg-secondary">Acts as</p>
                <p className="mt-1 mb-0 text-sm text-fg [overflow-wrap:anywhere]">
                  {apiKey.user.name.trim() || apiKey.user.email}
                </p>
              </div>
            </div>
          ) : (
            <div className="sm:col-span-2">
              <Field label="Purpose" required error={errors.purpose}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    name="purpose"
                    value={values.purpose}
                    maxLength={MAX_LENGTHS.purpose}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    invalid={Boolean(errors.purpose)}
                    aria-describedby={describedBy}
                    onChange={(event) => setField('purpose', event.target.value)}
                  />
                )}
              </Field>
            </div>
          )}

          {!isEdit && (
            <div className="sm:col-span-2">
              <Field label="Acts as" required error={userFieldError}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Combobox
                    id={id}
                    value={values.userId}
                    onChange={(next) => setField('userId', next)}
                    options={userOptions}
                    placeholder="Choose an account…"
                    searchPlaceholder="Search staff…"
                    loading={staff.loading}
                    /*
                     * A truncated directory is SAID rather than hidden: the
                     * picker filters the loaded page locally, so somebody past
                     * the cut simply does not appear and the reader concludes
                     * the account does not exist.
                     */
                    footnote={
                      staff.hasMore ? 'Showing the first page of staff accounts.' : undefined
                    }
                    invalid={Boolean(userFieldError)}
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
            </div>
          )}
        </div>

        <Field
          label="Scopes"
          error={errors.scopes}
          /*
           * ⚠️ Rendered ONLY while the set is empty, and it is a statement
           * about the value in front of the reader rather than advice or a
           * fault. **An empty scope set is a real and valid state** — a
           * credential that authenticates and can do nothing, which is a soft
           * revoke that starts working again the moment a scope is granted
           * back. It is also a plausible mistake and an invisible one: the save
           * succeeds, the key works, and every call it makes is refused.
           */
          hint={
            values.scopes.length === 0
              ? 'With no scopes this key can authenticate and do nothing else.'
              : undefined
          }
        >
          {({ id, 'aria-describedby': describedBy }) => (
            <MultiCombobox
              id={id}
              values={values.scopes}
              onChange={(next) => setField('scopes', asScopes(next))}
              options={scopeChoices}
              placeholder="No scopes"
              searchPlaceholder="Search scopes…"
              summaryNoun={{ singular: 'scope', plural: 'scopes' }}
              /*
               * The list is short, complete and never truncated — there is no
               * `footnote` on this control — so select-all cannot claim rows it
               * did not show. ⚠️ It selects the VISIBLE options, which includes
               * any unknown scope injected back by `scopeOptions`; that is
               * correct, since dropping one is the destruction this feature
               * guards against.
               */
              selectAll
              invalid={Boolean(errors.scopes)}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        <Field
          label="Rate limit (requests per minute)"
          error={errors.rateLimitPerMinute}
          /*
           * ⛔ **PROTECTED — this is not helper text.** An empty box cannot
           * tell a reader that blank is the global budget rather than zero, and
           * those are opposite instructions: blank is the most permissive
           * answer available and `0` is refused outright. Nothing else on this
           * form can say it, and the wrong conclusion it prevents — typing `0`
           * to mean "no limit" — is the one somebody reaches for first.
           *
           * ⚠️ The `0` case is an `error` rather than a second hint, because it
           * mirrors a REFUSAL the server makes. See `rateLimitError`.
           */
          hint={RATE_LIMIT_BLANK_MEANS_GLOBAL}
        >
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              name="rateLimitPerMinute"
              // Text with a numeric mode rather than type="number": the value
              // has to survive being half-typed, and a number input hands back
              // a coerced or locale-formatted one — which is exactly how a
              // blank box becomes a zero.
              inputMode="numeric"
              value={values.rateLimitPerMinute}
              placeholder="Global budget"
              invalid={Boolean(errors.rateLimitPerMinute)}
              aria-describedby={describedBy}
              onChange={(event) => setField('rateLimitPerMinute', event.target.value)}
            />
          )}
        </Field>
      </form>
    </Modal>
  );
}
